import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import './AdminEveningReview.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
});

function fmtUsd(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function fmtSigned(n) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  return `${v >= 0 ? '+' : ''}${fmtUsd(v)}`;
}

export default function AdminEveningReview() {
  const [settings, setSettings] = useState(null);
  const [latest, setLatest] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [draftEdit, setDraftEdit] = useState('');
  const [newPolicy, setNewPolicy] = useState({
    policyKey: '',
    title: '',
    category: 'general',
    notes: '',
    valueJson: '{}',
  });

  const load = useCallback(async () => {
    const [rev, pol] = await Promise.all([
      axios.get(`${API_BASE}/multi-asset/admin/evening-review`, { headers: authHeaders() }),
      axios.get(`${API_BASE}/multi-asset/admin/map-policies`, { headers: authHeaders() }),
    ]);
    setSettings(rev.data.settings);
    setLatest(rev.data.latest);
    setReviews(rev.data.reviews || []);
    setPolicies(pol.data.policies || []);
    if (rev.data.latest?.draftMap) {
      setDraftEdit(JSON.stringify(rev.data.latest.draftMap, null, 2));
    } else {
      setDraftEdit('');
    }
  }, []);

  useEffect(() => {
    load().catch((e) => setError(e.response?.data?.error || e.message));
  }, [load]);

  const saveSettings = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const recipients = String(settings.alertRecipientsText ?? (settings.alertRecipients || []).join(', '))
        .split(/[,;\s]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const { data } = await axios.patch(
        `${API_BASE}/multi-asset/admin/evening-review/settings`,
        {
          enabled: settings.enabled,
          quietAutoHold: settings.quietAutoHold,
          cutoffMinutes: Number(settings.cutoffMinutes) || 180,
          monthlyCapUsd: Number(settings.monthlyCapUsd) || 40,
          alertRecipients: recipients,
        },
        { headers: authHeaders() }
      );
      setSettings({
        ...data.settings,
        alertRecipientsText: (data.settings.alertRecipients || []).join(', '),
      });
      setMessage('Evening Review settings saved.');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  const runNow = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const { data } = await axios.post(
        `${API_BASE}/multi-asset/admin/evening-review/run-now`,
        {},
        { headers: authHeaders() }
      );
      setMessage(`Review #${data.review?.id} created (${data.review?.status}).`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  const decide = async (action) => {
    if (!latest?.id) return;
    setBusy(true);
    setError(null);
    try {
      let editedMap = null;
      if (action === 'approve' && draftEdit.trim()) {
        editedMap = JSON.parse(draftEdit);
      }
      const { data } = await axios.post(
        `${API_BASE}/multi-asset/admin/evening-review/${latest.id}/${action}`,
        action === 'approve' ? { map: editedMap } : {},
        { headers: authHeaders() }
      );
      setMessage(`${action} applied — status ${data.status}.`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  const checkAutoHold = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API_BASE}/multi-asset/admin/evening-review/check-auto-hold`,
        {},
        { headers: authHeaders() }
      );
      setMessage(data.applied ? 'Quiet auto-hold applied.' : `Skipped: ${data.reason}`);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  const togglePolicy = async (p) => {
    setBusy(true);
    try {
      await axios.patch(
        `${API_BASE}/multi-asset/admin/map-policies/${p.id}`,
        { enabled: !p.enabled },
        { headers: authHeaders() }
      );
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  const savePolicyNotes = async (p, notes) => {
    setBusy(true);
    try {
      await axios.patch(
        `${API_BASE}/multi-asset/admin/map-policies/${p.id}`,
        { notes },
        { headers: authHeaders() }
      );
      await load();
      setMessage(`Updated ${p.policyKey}`);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  const addPolicy = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      let value = {};
      try {
        value = JSON.parse(newPolicy.valueJson || '{}');
      } catch {
        throw new Error('valueJson must be valid JSON');
      }
      await axios.post(
        `${API_BASE}/multi-asset/admin/map-policies`,
        {
          policyKey: newPolicy.policyKey,
          title: newPolicy.title || newPolicy.policyKey,
          category: newPolicy.category,
          notes: newPolicy.notes,
          value,
        },
        { headers: authHeaders() }
      );
      setNewPolicy({ policyKey: '', title: '', category: 'general', notes: '', valueJson: '{}' });
      await load();
      setMessage('Policy created.');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  const removePolicy = async (p) => {
    if (!window.confirm(`Delete policy ${p.policyKey}?`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API_BASE}/multi-asset/admin/map-policies/${p.id}`, { headers: authHeaders() });
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!settings) {
    return (
      <section id="ma-evening-review" className="ma-admin-card ma-evening-card">
        <h3>Evening Review</h3>
        <p className="ma-inbox-loading">Loading…</p>
      </section>
    );
  }

  const s = {
    ...settings,
    alertRecipientsText:
      settings.alertRecipientsText ?? (settings.alertRecipients || []).join(', '),
  };

  return (
    <>
      <section id="ma-evening-review" className="ma-admin-card ma-evening-card">
        <h3>Evening Review (Phase 1)</h3>
        <p className="ma-card-desc">
          After each macro-chart send, Omni builds a draft MAP (Claude if configured, else mechanical hold). Quiet
          nights can auto-export a hold MAP after the cutoff. Gio should keep the Claude.ai ritual until this path is
          proven.
        </p>

        {error && <div className="error-message">{error}</div>}
        {message && <div className="ma-admin-success">{message}</div>}

        <form onSubmit={saveSettings} className="ma-inbox-form">
          <label className="ma-checkbox-label">
            <input
              type="checkbox"
              checked={!!s.enabled}
              onChange={(e) => setSettings({ ...s, enabled: e.target.checked })}
            />
            Evening Review enabled
          </label>
          <label className="ma-checkbox-label">
            <input
              type="checkbox"
              checked={!!s.quietAutoHold}
              onChange={(e) => setSettings({ ...s, quietAutoHold: e.target.checked })}
            />
            Quiet-night auto-hold (no human tap after cutoff)
          </label>
          <div className="form-row-two">
            <div className="form-group">
              <label>Cutoff minutes after draft</label>
              <input
                type="number"
                min={15}
                max={1440}
                value={s.cutoffMinutes}
                onChange={(e) => setSettings({ ...s, cutoffMinutes: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Claude monthly soft cap (USD)</label>
              <input
                type="number"
                min={5}
                max={500}
                step={1}
                value={s.monthlyCapUsd}
                onChange={(e) => setSettings({ ...s, monthlyCapUsd: e.target.value })}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Alert recipients (Gio + ops — comma-separated)</label>
            <input
              type="text"
              value={s.alertRecipientsText}
              onChange={(e) => setSettings({ ...s, alertRecipientsText: e.target.value })}
              placeholder="gio@…, ops@…"
            />
          </div>
          <p className="field-hint">
            Claude: {s.claude?.configured ? 'configured' : 'NOT configured (mechanical drafts only)'} · model{' '}
            {s.claude?.model || '—'} · month spend {fmtUsd(s.claude?.monthSpendUsd)} / {fmtUsd(s.claude?.monthlyCapUsd)}{' '}
            remaining {fmtUsd(s.claude?.remainingUsd)}
          </p>
          <div className="ma-evening-actions">
            <button type="submit" className="primary-btn" disabled={busy}>
              Save settings
            </button>
            <button type="button" className="secondary-btn" disabled={busy} onClick={runNow}>
              Run review now
            </button>
            <button type="button" className="secondary-btn" disabled={busy} onClick={checkAutoHold}>
              Check quiet auto-hold
            </button>
          </div>
        </form>

        {latest && (
          <div className="ma-evening-latest">
            <h4>
              Latest open review #{latest.id} · {latest.status}
              {latest.quietNight ? ' · quiet' : ' · needs eyes'}
            </h4>
            <p>
              Equity {fmtUsd(latest.brief?.totalEquityUsd)} · Day P&amp;L {fmtSigned(latest.brief?.dayPnlUsd)} · Cash{' '}
              {fmtUsd(latest.brief?.cashDeployableUsd)} · source {latest.brief?.source || '—'}
            </p>
            {(latest.needsDecision || []).length > 0 && (
              <ul className="ma-evening-needs">
                {latest.needsDecision.map((n, i) => (
                  <li key={`${n.type}-${i}`}>{n.detail || n.type}</li>
                ))}
              </ul>
            )}
            {latest.errorMessage && <p className="ma-evening-warn">{latest.errorMessage}</p>}
            <label>Draft MAP JSON (edit before Approve)</label>
            <textarea
              className="ma-evening-draft"
              rows={14}
              value={draftEdit}
              onChange={(e) => setDraftEdit(e.target.value)}
            />
            <div className="ma-evening-actions">
              <button type="button" className="primary-btn" disabled={busy} onClick={() => decide('approve')}>
                Approve → inbox
              </button>
              <button type="button" className="secondary-btn" disabled={busy} onClick={() => decide('hold')}>
                Hold → inbox
              </button>
            </div>
          </div>
        )}

        {!latest && <p className="field-hint">No open draft. Run review now or wait for the next macro send.</p>}

        {reviews?.length > 0 && (
          <div className="ma-evening-history">
            <h4>Recent reviews</h4>
            <table className="ma-evening-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Status</th>
                  <th>Quiet</th>
                  <th>Day P&amp;L</th>
                  <th>Cost</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {reviews.map((r) => (
                  <tr key={r.id}>
                    <td>{r.id}</td>
                    <td>{r.status}</td>
                    <td>{r.quietNight ? 'yes' : 'no'}</td>
                    <td>{fmtSigned(r.brief?.dayPnlUsd)}</td>
                    <td>{r.claudeCostUsd != null ? `$${Number(r.claudeCostUsd).toFixed(4)}` : '—'}</td>
                    <td>{r.createdAt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section id="ma-map-policies" className="ma-admin-card ma-policies-card">
        <h3>MAP standing policies</h3>
        <p className="ma-card-desc">
          Editable rules Gio can change anytime (oil/crypto bans, sizing, hedge cover). Evening agent reads enabled
          policies on every draft.
        </p>
        <ul className="ma-policies-list">
          {policies.map((p) => (
            <li key={p.id} className={!p.enabled ? 'disabled' : ''}>
              <div className="ma-policy-head">
                <strong>{p.title}</strong>
                <code>{p.policyKey}</code>
                <span className="ma-policy-cat">{p.category}</span>
                <button type="button" className="secondary-btn" disabled={busy} onClick={() => togglePolicy(p)}>
                  {p.enabled ? 'Disable' : 'Enable'}
                </button>
                <button type="button" className="secondary-btn" disabled={busy} onClick={() => removePolicy(p)}>
                  Delete
                </button>
              </div>
              <p className="ma-policy-value">{JSON.stringify(p.value)}</p>
              <input
                type="text"
                defaultValue={p.notes || ''}
                placeholder="Notes"
                onBlur={(e) => {
                  if (e.target.value !== (p.notes || '')) savePolicyNotes(p, e.target.value);
                }}
              />
            </li>
          ))}
        </ul>
        <form onSubmit={addPolicy} className="ma-inbox-form ma-policy-add">
          <h4>Add policy</h4>
          <div className="form-row-two">
            <div className="form-group">
              <label>Key</label>
              <input
                value={newPolicy.policyKey}
                onChange={(e) => setNewPolicy({ ...newPolicy, policyKey: e.target.value })}
                placeholder="allow_oil_trial"
                required
              />
            </div>
            <div className="form-group">
              <label>Title</label>
              <input
                value={newPolicy.title}
                onChange={(e) => setNewPolicy({ ...newPolicy, title: e.target.value })}
                placeholder="Allow oil temporarily"
              />
            </div>
          </div>
          <div className="form-row-two">
            <div className="form-group">
              <label>Category</label>
              <input
                value={newPolicy.category}
                onChange={(e) => setNewPolicy({ ...newPolicy, category: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Value JSON</label>
              <input
                value={newPolicy.valueJson}
                onChange={(e) => setNewPolicy({ ...newPolicy, valueJson: e.target.value })}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Notes</label>
            <input
              value={newPolicy.notes}
              onChange={(e) => setNewPolicy({ ...newPolicy, notes: e.target.value })}
            />
          </div>
          <button type="submit" className="primary-btn" disabled={busy}>
            Add policy
          </button>
        </form>
      </section>
    </>
  );
}
